<template>
  <div :data-view-id="domId" class="custom-view">
    <b-container fluid>
      <ViewTitle :title="title" />
      <b-row v-if="visiblePresetFilterButtons.length" class="pb-1">
        <b-col>
          <PresetFilterButtons
            :buttons="visiblePresetFilterButtons"
            :active-ids="activePresetFilterIds"
            @toggle="onPresetFilterToggle"
          />
        </b-col>
      </b-row>
      <b-row v-if="searchEnabled" class="pb-1">
        <b-col>
          <ViewSearch :model-value="searchText" @update:model-value="onSearchInput" />
        </b-col>
      </b-row>
      <b-row v-if="userFiltering" class="pb-1">
        <b-col>
          <RuntimeFilterBuilder
            ref="filterBuilder"
            :field-groups="filterFieldGroups"
            :initial-filters="resolvedInitialFilters"
            :initial-match-mode="resolvedInitialMatchMode"
            @filter-changed="onFilterChanged"
          />
        </b-col>
      </b-row>
      <p v-if="loadError" class="text-muted">{{ loadError }}</p>
      <p v-else-if="!hasCode" class="text-muted">This view has no code yet.</p>
      <p v-else-if="rendered.error" class="text-muted">{{ rendered.error }}</p>
      <div v-else class="custom-output" v-html="renderedDocument"></div>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios'
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import ViewTitle from './ViewTitle.vue'
import ViewSearch from './ViewSearch.vue'
import { realtimeSubscribeFields } from '../lib/view-visibility'
import { liveAppWebSocketUrl } from '../lib/live-app-socket'
import { viewSearchParam } from '../../../shared/view-search'
import { eventRefreshesView } from '../../../shared/view-refresh'
import { filterQueryHref, readFilterQuery } from '../lib/filter-query'
import PresetFilterButtons from './PresetFilterButtons.vue'
import { presetFilterMixin } from '../lib/preset-filter-state'
import {
  customViewContext,
  customViewDomId,
  customViewReady,
  customViewScopedCss,
  plainCustomViewRows,
  renderCustomView,
  sampleContextForCode,
  sanitizePreviewHtml
} from '../../../shared/custom-view'
import { sanitizeStyleText } from '../../../shared/html-sanitize'

export default {
  inheritAttrs: false,
  mixins: [presetFilterMixin],
  components: {
    RuntimeFilterBuilder,
    ViewTitle,
    ViewSearch,
    PresetFilterButtons
  },
  data () {
    return {
      records: [],
      loadError: '',
      filterFieldGroups: [],
      activeFilters: null,
      urlFilters: null,
      urlMatchMode: null,
      searchText: '',
      ws: null
    }
  },
  props: {
    uniqueTitle: { type: String, default: '' },
    title: { type: String, default: '' },
    viewCode: { type: String, default: '' },
    viewCss: { type: String, default: '' },
    userFiltering: { type: Boolean, default: false },
    realtime: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    pageUniqueTitle: { type: String, default: '' },
    componentUniqueTitle: { type: String, default: '' },
    defaultUserFilters: { type: Array, default: () => [] },
    defaultUserFilterMatchMode: { type: String, default: 'all' },
    restrictUserFiltering: { type: Boolean, default: false },
    searchEnabled: { type: Boolean, default: false }
  },
  computed: {
    domId () {
      return customViewDomId(this.componentUniqueTitle || this.uniqueTitle)
    },
    scopedCss () {
      return customViewScopedCss(this.componentUniqueTitle || this.uniqueTitle, this.viewCss)
    },
    hasCode () {
      return customViewReady(this.viewCode).ok
    },
    context () {
      if (window.__superturbo_preview_mode) return sampleContextForCode(this.viewCode, this.title)
      return customViewContext(this.records, this.title)
    },
    rendered () {
      if (!this.hasCode) return { ok: false, error: '', html: '' }
      return renderCustomView(this.viewCode, this.context)
    },
    renderedDocument () {
      const css = this.scopedCss ? `<style>${sanitizeStyleText(this.scopedCss)}</style>` : ''
      return css + sanitizePreviewHtml(this.rendered.html)
    },
    filterQueryKey () {
      return `f_${this.componentUniqueTitle || this.uniqueTitle}`
    },
    searchQueryKey () {
      return viewSearchParam(this.componentUniqueTitle || this.uniqueTitle)
    },
    searchTerm () {
      return String(this.searchText || '').trim()
    },
    resolvedInitialFilters () {
      if (this.urlFilters) return this.urlFilters
      return this.defaultUserFilters
    },
    resolvedInitialMatchMode () {
      if (this.urlMatchMode) return this.urlMatchMode
      return this.defaultUserFilterMatchMode
    }
  },
  mounted () {
    if (this.userFiltering && !window.__superturbo_preview_mode) {
      this.readFiltersFromUrl()
      this.loadFilterFields()
    }
    if (this.searchEnabled && !window.__superturbo_preview_mode) this.readSearchFromUrl()
    if (this.userFiltering && !window.__superturbo_preview_mode) {
      const filters = this.resolvedInitialFilters
      if (filters && filters.length) {
        this.activeFilters = { filters, matchMode: this.resolvedInitialMatchMode || 'all' }
      }
    }
    this.loadRecords(this.activeFilters)
    if (this.realtime && !window.__superturbo_preview_mode) this.connectRealtimeWebSocket()
    this._onRefreshViews = (event) => {
      if (!eventRefreshesView(event, this.componentUniqueTitle || this.uniqueTitle)) return
      this.loadRecords(this.activeFilters)
    }
    window.addEventListener('superturbo-refresh-views', this._onRefreshViews)
  },
  beforeUnmount () {
    this._left = true
    clearTimeout(this._searchTimer)
    this.disconnectRealtimeWebSocket()
    if (this._onRefreshViews) window.removeEventListener('superturbo-refresh-views', this._onRefreshViews)
  },
  methods: {
    reloadForPresetFilters () {
      return this.loadRecords(this.activeFilters)
    },
    async loadRecords (filterEvent) {
      if (window.__superturbo_preview_mode) {
        this.records = []
        this.loadError = ''
        return
      }

      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      const queryParts = ['limit=100']
      const filters = filterEvent || this.activeFilters

      if (filters && filters.filters && filters.filters.length > 0) {
        queryParts.push(`userFilters=${encodeURIComponent(JSON.stringify(filters.filters))}`)
        queryParts.push(`userFilterMatchMode=${encodeURIComponent(filters.matchMode || 'all')}`)
      }
      if (this.searchTerm) queryParts.push(`search=${encodeURIComponent(this.searchTerm)}`)
      if (this.presetFilterQuery()) queryParts.push(`presetFilters=${encodeURIComponent(this.presetFilterQuery())}`)
      const accessKey = this.routeAccessKey
      if (accessKey) queryParts.push(`accessKey=${encodeURIComponent(accessKey)}`)

      const url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}?${queryParts.join('&')}`
      const requestId = (this._loadId = (this._loadId || 0) + 1)

      try {
        const response = await axios.get(url)
        if (requestId !== this._loadId) return
        this.records = plainCustomViewRows(Array.isArray(response.data) ? response.data : [])
        this.loadError = ''
      } catch (err) {
        if (requestId !== this._loadId) return
        console.warn('Failed to load custom view:', err)
        this.loadError = 'This view could not be loaded.'
      }
    },
    async loadFilterFields () {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      if (!pageUT || !compUT) return
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''
      try {
        let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/filterfields`
        if (this.restrictUserFiltering) url += '?restrict=true'
        const response = await axios.get(url)
        this.filterFieldGroups = response.data
      } catch (err) {
        console.warn('Failed to load filter fields:', err)
      }
    },
    compactFilter (filter) {
      const compact = {}
      for (const [key, value] of Object.entries(filter)) {
        if (value === null || value === undefined) continue
        compact[key] = key === 'valueFieldRef' && value !== null ? this.compactFilter(value) : value
      }
      return compact
    },
    expandFilter (compact) {
      const full = {
        relationshipColumn: null,
        relatedSpreadsheet: null,
        sourceSpreadsheet: null,
        sourceRelationshipColumn: null,
        relQuantifier: null,
        valueType: 'literal',
        value: '',
        valueFieldRef: null,
        ...compact
      }
      if (full.valueFieldRef && typeof full.valueFieldRef === 'object') {
        full.valueFieldRef = this.expandFilter(full.valueFieldRef)
      }
      return full
    },
    filtersMatchDefaults (filterEvent) {
      const defaults = this.defaultUserFilters || []
      const current = (filterEvent && filterEvent.filters) || []
      const currentMode = (filterEvent && filterEvent.matchMode) || 'all'
      const defaultMode = this.defaultUserFilterMatchMode || 'all'
      if (currentMode !== defaultMode) return false
      if (current.length !== defaults.length) return false
      return JSON.stringify(current) === JSON.stringify(defaults)
    },
    readFiltersFromUrl () {
      const parsed = readFilterQuery(window.location.search, this.filterQueryKey)
      if (!parsed) return
      this.urlFilters = parsed.filters.map(filter => this.expandFilter(filter))
      this.urlMatchMode = parsed.matchMode
    },
    writeFiltersToUrl (filterEvent) {
      try {
        const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
        let payload = null
        if (hasFilters && !this.filtersMatchDefaults(filterEvent)) {
          payload = { filters: filterEvent.filters.map(filter => this.compactFilter(filter)) }
          if ((filterEvent.matchMode || 'all') !== 'all') payload.matchMode = filterEvent.matchMode
        }
        window.history.replaceState(null, '', filterQueryHref(window.location.href, this.filterQueryKey, payload))
      } catch (err) {
        console.warn('Failed to write filters to URL:', err)
      }
    },
    readSearchFromUrl () {
      try {
        const params = new URLSearchParams(window.location.search)
        this.searchText = params.get(this.searchQueryKey) || ''
      } catch (err) {
        this.searchText = ''
      }
    },
    writeSearchToUrl () {
      try {
        const url = new URL(window.location.href)
        if (this.searchTerm) url.searchParams.set(this.searchQueryKey, this.searchTerm)
        else url.searchParams.delete(this.searchQueryKey)
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
      } catch (err) {
        console.warn('Failed to write search to URL:', err)
      }
    },
    onSearchInput (value) {
      this.searchText = value
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.writeSearchToUrl()
        this.loadRecords(this.activeFilters)
      }, 300)
    },
    async onFilterChanged (filterEvent) {
      this.activeFilters = filterEvent
      this.writeFiltersToUrl(filterEvent)
      this.loadRecords(filterEvent)
    },
    connectRealtimeWebSocket () {
      try {
        this.ws = new WebSocket(liveAppWebSocketUrl(VUE_APP_APP_SERVER_WS_URL, this.routeAccount, this.$route.params.application))
      } catch (err) {
        console.warn('Realtime WebSocket connection failed:', err)
        return
      }
      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          type: 'realtime_subscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          ...realtimeSubscribeFields({
            ...(this.$route && this.$route.params),
            account: this.routeAccount
          })
        }))
      }
      this.ws.onmessage = (event) => {
        let msg
        try { msg = JSON.parse(event.data) } catch { return }
        if (msg.type === 'realtime_update' &&
            msg.pageUniqueTitle === this.pageUniqueTitle &&
            msg.componentUniqueTitle === this.componentUniqueTitle) {
          this.loadRecords(this.activeFilters)
        }
      }
      this.ws.onclose = () => {
        this.ws = null
        setTimeout(() => {
          if (this.realtime && !this._left) this.connectRealtimeWebSocket()
        }, 3000)
      }
    },
    disconnectRealtimeWebSocket () {
      if (!this.ws) return
      try {
        this.ws.send(JSON.stringify({
          type: 'realtime_unsubscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle
        }))
      } catch (err) {
        // The socket may already be closed.
      }
      this.ws.close()
      this.ws = null
    }
  }
}
</script>
